// Tick and tooltip labels for the console's time axes, and the span a chart's title
// names. Shared by Cost & Billing and Billing, which draw the same spend chart from
// different sources (live provider vs stored snapshot) and must label it identically
// — the month pair below was duplicated verbatim in both pages before the day pair
// existed, and two copies of a tick rule is exactly how two charts come to disagree
// about what "Aug" means.
//
// Two grains, one rule between them: a tick carries the *coarser* unit only where it
// changes, so an axis says where it turned over without repeating itself on every
// column, and the tooltip always spells the bucket out in full — an abbreviated tick
// is ambiguous on its own, and the tooltip is where the reader goes to disambiguate.

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// YYYY-MM → its parts. An unparseable value yields no name, and every formatter
// below falls back to the raw string rather than printing "undefined".
const monthParts = (m) => {
  const [year, month] = String(m).split('-');
  return { year, name: MONTH_NAMES[Number(month) - 1], isJanuary: month === '01' };
};

// YYYY-MM-DD → its parts, including the day as a number for the tick.
const dayParts = (d) => {
  const [year, month, day] = String(d).split('-');
  return {
    year,
    month,
    day: String(Number(day)),
    name: MONTH_NAMES[Number(month) - 1],
    isFirst: day === '01',
  };
};

// Month axis: the year rides on the first column and on each January, so a
// twelve-month axis says where it turned over without carrying the year twelve times.
export const monthTick = (m, i) => {
  const { year, name, isJanuary } = monthParts(m);
  if (!name) return m;
  return i === 0 || isJanuary ? `${name.slice(0, 3)} ’${year.slice(2)}` : name.slice(0, 3);
};

// Month tooltip: spelled out, because "Aug" alone is ambiguous once the axis has
// abbreviated it.
export const monthFull = (m) => {
  const { year, name } = monthParts(m);
  return name ? `${name} ${year}` : m;
};

// Day axis: the month rides on the first column and on each 1st — the same rule the
// month axis applies to the year, one unit down. The day number alone would restart
// at 1 in the middle of an axis with nothing saying why.
export const dayTick = (d, i) => {
  const { name, day, isFirst } = dayParts(d);
  if (!name) return d;
  return i === 0 || isFirst ? `${day} ${name.slice(0, 3)}` : day;
};

// Day tooltip: the full date, since a tick of "14" says nothing on its own and the
// year is never on the day axis at all.
export const dayFull = (d) => {
  const { year, name, day } = dayParts(d);
  return name ? `${day} ${name} ${year}` : d;
};

// The span a day chart's title names, e.g. "1 – 8 Aug 2026". The month and year are
// printed once where both ends share them, because the common case is a window
// inside one month and "1 Aug 2026 – 8 Aug 2026" reads as two unrelated dates.
export const dayRange = (from, to) => {
  const a = dayParts(from);
  const b = dayParts(to);
  if (!a.name || !b.name) return [from, to].filter(Boolean).join(' – ');
  const short = (p) => p.name.slice(0, 3);
  if (a.year === b.year && a.month === b.month) {
    return `${a.day} – ${b.day} ${short(b)} ${b.year}`;
  }
  if (a.year === b.year) {
    return `${a.day} ${short(a)} – ${b.day} ${short(b)} ${b.year}`;
  }
  return `${a.day} ${short(a)} ${a.year} – ${b.day} ${short(b)} ${b.year}`;
};
